import type { FC, ReactNode } from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Inputs sit on dark cards, so these styles assume light text on ink. */
export const inputClasses = 'min-h-12 w-full rounded-xl border border-cream/20 bg-cream/[0.06] px-4 text-base text-cream placeholder:text-cream/40 outline-none transition-[border-color,box-shadow] focus:border-lime focus:ring-2 focus:ring-lime/35 aria-[invalid=true]:border-coral'

interface FieldProps {
    id: string
    label: string
    error?: string
    hint?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ id, label, error, hint, optional, className, children }): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 text-sm font-medium text-cream/90'>
                {label}
                {optional && <span className='font-mono text-[10px] uppercase tracking-[0.18em] text-cream/50'>Optional</span>}
            </label>

            {children}

            {error
                ? (
                        <p id={`${id}-error`} className='text-xs font-medium text-coral'>
                            {error}
                        </p>
                    )
                : hint && (
                    <p id={`${id}-hint`} className='text-xs text-cream/55'>
                        {hint}
                    </p>
                )}
        </div>
    )
}

interface SelectShellProps {
    children: ReactNode
}

/** Wraps a native select so it gets the chevron and the dark styling. */
export const SelectShell: FC<SelectShellProps> = ({ children }): ReactNode => {
    return (
        <div className='relative'>
            {children}
            <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-cream/60' />
        </div>
    )
}

export const selectClasses = cn(inputClasses, 'cursor-pointer appearance-none pr-11 [&_option]:bg-ink [&_option]:text-cream')

/** Off-screen field that only bots fill in. */
export const HoneypotField: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
            <label htmlFor='website-field'>Leave this empty</label>
            <input id='website-field' type='text' name='website' tabIndex={-1} autoComplete='off' defaultValue='' />
        </div>
    )
}
